import React, { useRef, useCallback } from "react";
import PhoneInput from "react-phone-input-2";
import { Form, Input, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { isValidPhoneNumber } from "libphonenumber-js";
import toast from "react-hot-toast";
import { save } from "../../../features/auth/authSlice";
import SeoHead from "../../../components/SeoHead";

const { TextArea } = Input;

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

async function getCaptchaToken(action) {
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(SITE_KEY, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}

function RequestDemo() {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { saving } = useSelector((state) => state.auth);

  const onFinish = useCallback(async (values) => {
    let captchaToken;
    try {
      captchaToken = await getCaptchaToken("DEMO_REQUEST");
    } catch {
      toast.error("reCAPTCHA not ready. Please try again.");
      return;
    }

    const res = await dispatch(
      save({
        ...values,
        drPhone: values.drPhone.startsWith("+") ? values.drPhone : `+${values.drPhone}`,
        captchaToken,
        url: "api/v1/demo-request/save-demo-request",
      })
    );
    if (res?.payload?.success) {
      toast.success(res?.payload?.messages?.message || "Demo request submitted!");
      form.resetFields();
    } else {
      toast.error(res?.payload?.messages?.message || "Failed to submit. Please try again.");
    }
  }, [dispatch, form]);

  return (
    <div className="mt-[5rem] flex items-center justify-center bg-img-2 h-auto lg:py-[10rem] py-3 lg:px-0 px-3">
      <SeoHead
        title="Request a Demo"
        description="See Impala Digital Payments in action. Request a free demo and our team will show you how to streamline your business payments, petty cash and payroll."
        path="/request-demo"
      />
      <div className="flex flex-col items-center w-full max-w-[35rem]">
        <div className="flex items-center text-ink font-interSans text-[3rem] font-[400]">
          Request a&nbsp;
          <span className="font-[700] text-impalaRed">Demo!</span>
        </div>
        <div className="mt-[.5rem] w-full text-center text-ink text-[1rem] font-[400] leading-[1.625rem]">
          See how Impala Digital Payments works for your business. Fill in the form and our team
          will be in touch.
        </div>

        <Form
          layout="vertical"
          ref={formRef}
          name="request-demo"
          onFinish={onFinish}
          scrollToFirstError
          form={form}
          className="w-full mt-[2rem]"
        >
          <Form.Item
            label="Full Name"
            name="drName"
            rules={[{ required: true, message: "Full name is required" }]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            label="Organisation Name"
            name="drOrgName"
            rules={[{ required: true, message: "Organisation name is required" }]}
          >
            <Input className="input" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="drPhone"
            validateFirst
            rules={[
              { required: true, message: "Phone number is required" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.reject(new Error("Phone number is required"));
                  const phoneWithPlus = value.startsWith("+") ? value : `+${value}`;
                  if (isValidPhoneNumber(phoneWithPlus)) return Promise.resolve();
                  return Promise.reject(
                    new Error("Please enter a valid phone number for the selected country")
                  );
                },
              },
            ]}
          >
            <PhoneInput
              enableSearch
              country="ke"
              countryCodeEditable={false}
              className="input"
            />
          </Form.Item>

          <Form.Item
            label="Email"
            name="drEmail"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input type="email" className="input" />
          </Form.Item>

          <Form.Item
            label="Message"
            name="drMessage"
            rules={[{ required: true, message: "Message is required" }]}
          >
            <TextArea rows={4} className="text_area" />
          </Form.Item>

          <button
            className="cstm-btn-2 mt-[2rem] lg:!w-[14.5rem] w-auto"
            type="submit"
            disabled={saving}
          >
            {saving ? <Spin /> : "Submit Request"}
          </button>
        </Form>
      </div>
    </div>
  );
}

export default RequestDemo;
